import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CardActions, CardContent, CardMedia, Grid, IconButton, Tooltip, Typography } from '@material-ui/core';
import Button from '../../CustomButtons/Button.js';
import StudentPlannerIcon from '../../../assets/img/planner-workbook-icon.svg';
import MakingDecisionsImg from '../../../assets/img/workbook_images/making_decisions.jpg';
import IndividualPlanImg from '../../../assets/img/workbook_images/individual_plan.jpg';
import BudgetingImg from '../../../assets/img/workbook_images/budgeting.jpg';
import HousingImg from '../../../assets/img/workbook_images/housing.jpg';
import HealthCareImg from '../../../assets/img/workbook_images/healthcare.jpg';
import RelationshipsImg from '../../../assets/img/workbook_images/relationships.jpg';
import TrophyImg from '../../../assets/img/workbook_images/trophy.svg';
import TransportationImg from '../../../assets/img/workbook_images/transportation.jpg';
import MovingImg from '../../../assets/img/workbook_images/moving.jpg';
import Link from '@material-ui/core/Link';
import HomeIcon from '@material-ui/icons/Home';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import { btLightOrange, whiteColor } from '../../../assets/jss/nextjs-material-kit-pro';
import { calculateOverallCompletionPercentage, getModuleCompletionStatus, isModuleCompleted } from '../../../util/workbookUtils';
import Paper from '@material-ui/core/Paper';
import { StyledBox, useWorkbookStyles } from './Workbook.styles';
import { isAdmin, isAdminOrFHT, isFHT } from '../../../util/miscUtil';

const IMAGE_MAP = {
  'making_decisions.jpg': MakingDecisionsImg,
  'individual_plan.jpg': IndividualPlanImg,
  'budgeting.jpg': BudgetingImg,
  'housing.jpg': HousingImg,
  'healthcare.jpg': HealthCareImg,
  'relationships.jpg': RelationshipsImg,
  'transportation.jpg': TransportationImg,
  'moving.jpg': MovingImg,
};

const Workbook = ({
  workbookModuleList,
  allResources,
  openWorkbookModule,
  setPage,
  classes,
  handleSetButtonColorByStatus,
  fetchButtonCompletionStatus,
  roles,
  senior,
  alumni,
  handleReturnToSeniorList,
  handleGotoAdminHomepage,
  refreshWorkbookStatus,
  isStaffMode = !!isAdminOrFHT(roles),
  isFHTMode = !!isFHT(roles),
}) => {
  window.scrollTo({ top: 0 });

  const workbookClasses = useWorkbookStyles();

  useEffect(() => {
    if (typeof refreshWorkbookStatus === 'function') {
      refreshWorkbookStatus();
    }
  }, []);

  const getImage = (filename) => {
    const imgSrc = IMAGE_MAP[filename];
    if (!imgSrc) return null;

    return <CardMedia component='img' alt='course image' height='220' image={imgSrc} />;
  };

  const checkModuleCoursesLength = (mod) => {
    if (!mod) return 0;
    let totalCourses = 0;
    mod.steps?.forEach((step) => {
      totalCourses = totalCourses + step.courses.length;
    });
    return totalCourses;
  };

  return (
    <React.Fragment>
      <summary className={classes.workbookHeaderContent}>
        <Box
          sx={{
            width: '100%',
            maxWidth: {
              xs: '380px',
              sm: '540px',
              md: '868px',
              lg: '1320px',
              xl: '1320px',
            },
          }}
        >
          <Grid container direction='row' style={{ width: '100%' }} align='center'>
            <Grid item xs={false} sm={1} md={1} lg={1} align='center' className={classes.logoContainer}>
              <img src={StudentPlannerIcon} alt='Student Planner Workbook Icon' className={classes.logo} />
            </Grid>
            <Grid item xs={12} sm={11} md={11} lg={11} align='center'>
              <Grid container spacing={0} direction='row' style={{ width: '100%' }}>
                <Grid item xs={9} sm={9} md={9} lg={10} align='left' style={{ width: '100%' }}>
                  <div className={classes.headerContent}>
                    <div className={classes.headerText}>
                      <div className={classes.title}>{isStaffMode || isFHTMode ? 'My Planning Workbook' : 'Planning Workbook'}</div>
                    </div>
                  </div>
                </Grid>
                <Grid item xs={3} sm={3} md={3} lg={2} align='center' style={{ width: '100%' }}>
                  {!isAdmin(roles) && (
                    <div style={{ marginTop: '-10px' }}>
                      <Tooltip title='Click to return to the planner home page.' placement='bottom'>
                        <IconButton color='inherit' aria-label='return home' onClick={() => setPage('home')}>
                          <Paper className={classes.circleIcon} elevation={1}>
                            <HomeIcon className={classes.headerHomeIcon} />
                          </Paper>
                        </IconButton>
                      </Tooltip>
                    </div>
                  )}
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} align='left' style={{ width: '100%', padding: '10px 0' }}>
                  <hr className={workbookClasses.lineStyle} />
                </Grid>
                {!isStaffMode && (
                  <Grid item xs={12} sm={12} md={12} lg={12} align='left' style={{ width: '95%' }}>
                    <div className={classes.headerContent}>
                      <div className={classes.headerText}>
                        <span className={classes.introduction}>
                          Your future, your way! Get the tools, tips, and confidence you need to plan your path and transition towards independence
                          with our comprehensive guide.
                        </span>
                      </div>
                    </div>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Box>

        <Grid direction='row' style={{ width: '100%', visibility: isStaffMode || isFHTMode ? 'hidden' : 'visible' }} align='center'>
          <Grid item xs={12}>
            <Box className={workbookClasses.workbookTrophy}>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: {
                    xs: '400px',
                    sm: '540px',
                    md: '785px',
                    lg: '1150px',
                    xl: '1300px',
                  },
                }}
              >
                <Grid container spacing={0} align='left'>
                  <Grid item xs={12} sm={12} md={5} lg={4} xl={4} align='left'>
                    <Typography className={workbookClasses.percentCompleteTitle}>{`${calculateOverallCompletionPercentage(
                      workbookModuleList
                    )} Complete`}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={7} lg={6} xl={6} align='left'>
                    <Box display='flex'>
                      {workbookModuleList?.map((mod) => (
                        <StyledBox
                          component='span'
                          key={mod.id}
                          className={workbookClasses.trophyBox}
                          style={{
                            WebkitMaskImage: `url(${TrophyImg})`,
                            maskImage: `url(${TrophyImg})`,
                            backgroundColor: isModuleCompleted(mod) ? btLightOrange : whiteColor,
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </summary>

      {/* Main Page */}
      <section className={classes.menuRestricted}>
        <Grid container spacing={0} direction='row'>
          <Grid item xs={12} align='left'>
            {isAdminOrFHT(roles) && (
              <div className={classes.breadcrumbs}>
                <Breadcrumbs separator='›' aria-label='breadcrumbs' className={classes.alignCrumb}>
                  <span className={classes.breadCrumbLink} onClick={() => handleGotoAdminHomepage()}>
                    <HomeIcon style={{ fontSize: '1.30rem' }} />
                    <span style={{ margin: '1px 0 0 10px' }}>Home</span>
                  </span>
                  {!isStaffMode && (
                    <span className={classes.breadCrumbLink} onClick={() => handleReturnToSeniorList()}>
                      {alumni ? 'Alumni' : 'High School Seniors'}
                    </span>
                  )}
                  {!isStaffMode && (
                    <span className={classes.breadCrumbLink} style={{ marginTop: '-1px' }} onClick={() => setPage('home')}>
                      {senior && senior.alumni_updated_first_name ? senior.alumni_updated_first_name : senior.first_name}{' '}
                      {senior && senior.alumni_updated_last_name ? senior.alumni_updated_last_name : senior.last_name}
                    </span>
                  )}
                  <span className={classes.breadCrumbText} style={{ marginTop: '-1px' }}>
                    {isStaffMode || isFHTMode ? 'My Planning Workbook' : 'Planning Workbook'}
                  </span>
                </Breadcrumbs>
              </div>
            )}
          </Grid>
        </Grid>
        <div style={{ margin: '0 auto 0 auto' }}>
          <div className={classes.workbookCenterMenu}>
            <Grid container spacing={3} direction='row' className={classes.menuRestricted}>
              <Grid item xs={12} align='center' className={classes.moduleGrid} style={{ width: '100%' }}>
                {workbookModuleList?.map((mod) => {
                  return (
                    <Card className={classes.moduleCard} key={mod.id}>
                      <CardContent className={classes.moduleCardBody}>
                        <section className={classes.moduleName}>{`Module ${mod.module_number}: ${mod.module_name}`}</section>
                      </CardContent>
                      {getImage(mod.image_name)}

                      {checkModuleCoursesLength(mod) > 0 ? (
                        <>
                          <CardActions className={classes.moduleActionContainer}>
                            <section className={classes.moduleButtonContainer}>
                              {isFHTMode ? (
                                <Button
                                  className={classes.workbookButton}
                                  round
                                  color='secondary'
                                  variant='contained'
                                  onClick={() => openWorkbookModule(mod.id, mod.module_number)}
                                >
                                  <a style={{ color: '#FFF' }}>View</a>
                                </Button>
                              ) : (
                                <Button
                                  className={classes.workbookButton}
                                  color={handleSetButtonColorByStatus(getModuleCompletionStatus(mod))}
                                  variant='contained'
                                  round
                                  onClick={() => openWorkbookModule(mod.id, mod.module_number)}
                                >
                                  <a style={{ color: 'rgba(255, 255, 255, 1)' }}>{fetchButtonCompletionStatus(getModuleCompletionStatus(mod))}</a>
                                </Button>
                              )}
                            </section>
                          </CardActions>
                        </>
                      ) : (
                        <summary className={classes.comingSoon}>Coming Soon</summary>
                      )}
                    </Card>
                  );
                })}
              </Grid>
            </Grid>
          </div>
        </div>
      </section>

      {allResources && allResources.length > 0 && (
        <div className={classes.resourcesBackground}>
          <summary className={classes.resources}>
            <div className={classes.resourceHeader}>Resources</div>
            <Grid container spacing={3} className={classes.resourceMenu}>
              <Grid item xs={12} align='left' className={classes.resourceGrid}>
                {allResources?.map((resource) => {
                  return (
                    <Card className={classes.resourceCard} key={resource.id}>
                      <CardContent className={classes.resourceCardBody}>
                        <div className={classes.resourceCardHeader}>
                          <section className={classes.resourceName}>{resource.resource_name}</section>
                        </div>
                        <section className={classes.resourceDescription}>{resource.resource_description.substring(0, 130)}</section>
                      </CardContent>
                      <CardActions className={classes.resourceActions}>
                        <Link href={resource.resource_url} target='_blank'>
                          <Button round color='info' size='sm' variant='contained' style={{ margin: '0 5px 0 0' }} className={classes.resourceButton}>
                            <a style={{ color: '#FFF' }}>View</a>
                          </Button>
                        </Link>
                      </CardActions>
                    </Card>
                  );
                })}
              </Grid>
            </Grid>
          </summary>
        </div>
        // </div>
      )}
      <div className={classes.centerMenu}>
        <div className={classes.returnButtonContainer}>
          <Button link onClick={() => setPage('home')} className={classes.returnButton}>
            <a style={{ color: 'rgba(0, 85, 128, 1)' }}>
              <u>Return to Home Page</u>
            </a>
          </Button>
        </div>
      </div>
    </React.Fragment>
  );
};

Workbook.propTypes = {
  workbookModuleList: PropTypes.array,
  allResources: PropTypes.array,
  openWorkbookModule: PropTypes.func,
  setPage: PropTypes.func,
  classes: PropTypes.object.isRequired,
  handleSetButtonColorByStatus: PropTypes.func.isRequired,
  fetchButtonCompletionStatus: PropTypes.func.isRequired,
  roles: PropTypes.array.isRequired,
  senior: PropTypes.object.isRequired,
  alumni: PropTypes.bool,
  handleReturnToSeniorList: PropTypes.func,
  handleGotoAdminHomepage: PropTypes.func.isRequired,
  refreshWorkbookStatus: PropTypes.func,
  isStaffMode: PropTypes.bool,
  isFHTMode: PropTypes.bool,
};

export default Workbook;
